import { gql, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { ChannelCardChannelFragment } from 'src/app/graph/graphSettingsPage/reportingPage/channelCard/ChannelCard';
import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ChannelInput } from '../ChannelInput';
import { PagerDutyRoutingKeyToolTip } from '../pagerDutyRoutingKeyToolTip/PagerDutyRoutingKeyToolTip';

interface Props {
  graphId: string;
  isOpen: boolean;
  onClose: () => void;
}

const upsertPagerDutyChannelMutation = gql`
  mutation UpsertPagerDutyChannelMutation(
    $graphId: ID!
    $channelId: ID
    $channel: PagerDutyChannelInput!
  ) {
    service(id: $graphId) {
      upsertPagerDutyChannel(id: $channelId, channel: $channel) {
        id
        ...ChannelCardChannelFragment
      }
    }
  }
  ${ChannelCardChannelFragment}
`;

export const integrationKeyRegex = /^[a-zA-Z0-9]{32}$/;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Please provide a name for the channel.'),
  routingKey: Yup.string()
    .required('Please provide an integration key.')
    .matches(integrationKeyRegex, 'Please provide a valid integration key.'),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

export const CreatePagerDutyChannelModal = ({
  graphId,
  isOpen,
  onClose,
}: Props) => {
  const [upsertPagerDutyChannel, { error: upsertError }] = useMutation<
    GraphQLTypes.UpsertPagerDutyChannelMutation,
    GraphQLTypes.UpsertPagerDutyChannelMutationVariables
  >(upsertPagerDutyChannelMutation);
  const { isSubmitting, values, errors, touched, handleSubmit, handleChange } =
    useFormik<FormValues>({
      initialValues: {
        name: '',
        routingKey: '',
      },
      async onSubmit(formValues) {
        const newChannel = await upsertPagerDutyChannel({
          variables: {
            graphId,
            channel: {
              name: formValues.name,
              routingKey: formValues.routingKey,
            },
          },
          update: (cache, { data }) => {
            cache.modify({
              id: cache.identify({ __typename: 'Service', id: graphId }),
              fields: {
                channels(cachedChannels = []) {
                  const newChannelRef = cache.writeFragment({
                    data: data?.service?.upsertPagerDutyChannel,
                    fragment: ChannelCardChannelFragment,
                  });
                  return [...cachedChannels, newChannelRef];
                },
              },
            });
          },
        });
        const newChannelId =
          newChannel.data?.service?.upsertPagerDutyChannel?.id;
        if (!newChannelId) {
          return;
        }
        onClose();
      },
      validationSchema,
    });
  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Configure PagerDuty Integration</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <ChannelInput
              autoFocus={true}
              inputName="name"
              label={
                <div className="flex items-center">
                  Channel Name
                  <Tooltip label="Choose a name to refer to this PagerDuty channel from Studio.">
                    <IconInfo className="ml-2 cursor-pointer text-secondary" />
                  </Tooltip>
                </div>
              }
              isInvalid={!!(touched.name && errors.name)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="#graph-manager-reports"
              value={values.name}
              error={errors.name}
            />
            <ChannelInput
              inputName="routingKey"
              label={
                <div className="flex items-center">
                  PagerDuty Integration Key
                  <PagerDutyRoutingKeyToolTip />
                </div>
              }
              isInvalid={!!(touched.routingKey && errors.routingKey)}
              isDisabled={isSubmitting}
              handleChange={handleChange}
              placeholder="integration key"
              value={values.routingKey}
              error={errors.routingKey}
            />

            {upsertError && <ErrorMessage />}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                type="button"
                isDisabled={isSubmitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Done"
                type="submit"
              >
                Done
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
